/**
 * Rate-limit calibration.
 *
 * The premise of the whole tab: `configured_qps` is a number this project
 * picked, not one any registry published. Registries do not document their RDAP
 * limits, so the figure stays an untested guess until a 429 actually appears.
 *
 * The page is built as a procedure rather than a dashboard because the numbers
 * are meaningless out of order — reading counters that still contain the
 * previous setting's results, or raising a limit that was never the constraint,
 * both produce confident nonsense.
 */
import * as React from 'react'
import { AlertTriangle, Eraser, Gauge, Play, RefreshCw } from 'lucide-react'
import {
  useGenerateCalibrationLoad, useResetResolverLimits, useResolverLimits, useSetResolverLimit, useTlds,
  type ResolverLimits,
} from '@/lib/queries'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label, Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { formatNumber } from '@/lib/utils'

type Host = ResolverLimits['rdap_hosts'][number]
type Verdict = Host['verdict']

/** What each verdict means for the next action, not just what it reports. */
const VERDICT: Record<Verdict, { label: string; variant: 'muted' | 'secondary' | 'warning' | 'danger'; next: string }> = {
  idle: {
    label: '无流量',
    variant: 'muted',
    next: '窗口内没有请求。先造压力。',
  },
  headroom: {
    label: '未打满',
    variant: 'secondary',
    next: '连自己配的 qps 都没跑满，此时调高限制测不出任何东西。加大压力。',
  },
  self_limited: {
    label: '自我限制中',
    variant: 'warning',
    next: '已站在自己的限制上，且上游从未拒绝过 — 可以调高一档再测。',
  },
  at_upstream_limit: {
    label: '已达上游上限',
    variant: 'danger',
    next: '上游开始拒绝请求（429 或 403）。退回上一档作为长期配置，不要继续加压。',
  },
  // Checked before the counters: an open breaker blocks requests locally, so
  // nothing reaches the limiter and nothing is counted — which otherwise reads
  // exactly like no traffic, and would tell you to go generate some.
  breaker_open: {
    label: '熔断已打开',
    variant: 'danger',
    next: '连续失败触发保护，请求在本地被拦下，因此计数停止。这本身就是踩线信号：退回上一个稳定档，等约 1 分钟熔断半开后再清零重测。',
  },
}

/**
 * A bucket smaller than one second of refill has no burst tolerance at all —
 * work arrives batched from the sweep, so this shows up as needless queueing.
 * Warned rather than blocked: it is legal, just almost never intended.
 */
function burstLooksLow(qps: string, burst: string): boolean {
  const q = Number(qps)
  const b = Number(burst)
  return Number.isFinite(q) && Number.isFinite(b) && q > 0 && b > 0 && b < q
}

/**
 * Inline budget editor.
 *
 * Saving takes effect on the next lookup rather than the next deploy, because
 * the resolver resolves the budget per request. That is what makes calibration
 * practical: restarting to apply each step would interrupt the very experiment
 * the step belongs to.
 */
function BudgetEditor({ host }: { host: Host }) {
  const toast = useToast()
  const save = useSetResolverLimit()
  const [qps, setQps] = React.useState(String(host.configured_qps))
  const [burst, setBurst] = React.useState(String(host.configured_burst))

  // Re-sync when the server value changes underneath us (another admin, or a
  // reset) — but not while this row is being edited.
  const dirty = qps !== String(host.configured_qps) || burst !== String(host.configured_burst)
  React.useEffect(() => {
    if (!dirty) {
      setQps(String(host.configured_qps))
      setBurst(String(host.configured_burst))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host.configured_qps, host.configured_burst])

  const commit = (body: { qps?: number; burst?: number; clear?: boolean }) => {
    save.mutate(
      { host: host.host, note: `校准于 ${new Date().toISOString().slice(0, 10)}`, ...body },
      {
        onSuccess: (r) => toast.success('已生效', r.note),
        onError: (e) => toast.error('保存失败', e.message),
      },
    )
  }

  return (
    <div className="space-y-1">
      <div className="flex items-end gap-2">
        <label className="space-y-0.5">
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">qps</span>
          <Input
            aria-label={`${host.host} 每秒请求数`} className="h-8 w-16 text-right" inputMode="decimal"
            value={qps} onChange={(e) => setQps(e.target.value)}
          />
        </label>
        <label className="space-y-0.5">
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">burst</span>
          <Input
            aria-label={`${host.host} 突发容量`} className="h-8 w-16 text-right" inputMode="numeric"
            value={burst} onChange={(e) => setBurst(e.target.value)}
          />
        </label>
        <Button
          size="sm" variant={dirty ? 'default' : 'ghost'} className="h-8"
          disabled={!dirty || save.isPending}
          onClick={() => commit({ qps: Number(qps), burst: Number(burst) })}
        >
          保存
        </Button>
      </div>
      {burstLooksLow(qps, burst) && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          burst 小于 qps：桶容量装不下一秒的配额，等于没有突发缓冲。建议设为 qps 的 2 倍。
        </p>
      )}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {host.source === 'override' ? (
          <>
            <Badge variant="default">已覆盖</Badge>
            <button className="underline hover:text-foreground" onClick={() => commit({ clear: true })}>
              恢复配置值
            </button>
          </>
        ) : (
          <span>来自配置文件</span>
        )}
      </div>
      {host.note && <p className="text-xs text-muted-foreground">{host.note}</p>}
    </div>
  )
}

export function RateLimitCalibration() {
  const toast = useToast()
  const [poll, setPoll] = React.useState(false)
  const [tld, setTld] = React.useState('')
  const [count, setCount] = React.useState('500')

  const { data, isLoading, error, refetch, isFetching } = useResolverLimits(true, poll)
  const { data: tldData } = useTlds()
  const reset = useResetResolverLimits()
  const load = useGenerateCalibrationLoad()

  const tlds = React.useMemo(
    () => (tldData?.tlds ?? []).filter((t) => t.enabled && t.rdap_supported),
    [tldData],
  )

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (isLoading) return <LoadingState rows={6} />
  if (!data) return null

  // Mirrors the server's brakes so the button is disabled before it is pressed
  // rather than reporting a refusal afterwards. The server still enforces them:
  // this is guidance, not the gate.
  const selected = data.rdap_hosts.find((h) => h.tlds.includes(tld))
  const brake = ((): string | null => {
    if (data.resolve_backlog > data.max_backlog_forload) {
      return `解析队列已积压 ${formatNumber(data.resolve_backlog)} 个任务。这些请求已经排定，无论现在做什么都会发出去，再加压等于叠加看不见的压力。等积压排空后再试。`
    }
    if (!selected) return null
    if (selected.cooling_until) {
      const mins = Math.max(1, Math.ceil((Date.parse(selected.cooling_until) - Date.now()) / 60000))
      return `${selected.host} 刚被上游拒绝过，冷却中，还需约 ${mins} 分钟。拒绝通常会升级为对来源地址的临时封禁：此时测出的是封禁状态而不是新 qps，继续加压还可能让封禁计时重新开始。`
    }
    if (selected.rejection_rate > data.max_rejection_rate) {
      return `${selected.host} 当前拒绝率 ${Math.round(selected.rejection_rate * 100)}%，已超过 ${Math.round(data.max_rejection_rate * 100)}% 上限。先下调 qps 并清零窗口。`
    }
    return null
  })()

  const onReset = () => {
    reset.mutate(undefined, {
      onSuccess: (r) => toast.success('窗口已清零', `清理了 ${r.scopes_cleared} 个 scope`),
      onError: (e) => toast.error('清零失败', e.message),
    })
  }

  const onLoad = () => {
    const n = Number(count)
    if (!tld || !Number.isFinite(n) || n <= 0) {
      toast.warning('请选择 TLD 并填写数量')
      return
    }
    load.mutate({ tld, count: n }, {
      onSuccess: (r) => {
        // Turn on polling automatically: an experiment has just started, and
        // the whole point is watching the counters move.
        setPoll(true)
        toast.success(`已标记 ${formatNumber(r.marked)} 个域名到期`, r.note)
      },
      onError: (e) => toast.error('加压失败', e.message),
    })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge className="h-4 w-4" aria-hidden />
            这个页面在测什么
          </CardTitle>
          <CardDescription>{data.note}</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-inside list-decimal space-y-1 text-sm text-muted-foreground">
            {data.how_to_calibrate.map((step) => (
              <li key={step}>{step.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>校准操作</CardTitle>
            <CardDescription>
              顺序有意义：先清零，再加压，然后等读数稳定。跳过清零会把上一档的结果混进这一档。
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="poll" className="text-xs text-muted-foreground">自动刷新</Label>
            <Switch id="poll" checked={poll} onCheckedChange={setPoll} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <Button variant="outline" onClick={onReset} disabled={reset.isPending}>
              <Eraser className="mr-2 h-4 w-4" aria-hidden />
              1. 清零窗口
            </Button>

            <div className="space-y-1">
              <Label htmlFor="tld" className="text-xs">TLD</Label>
              <Select value={tld} onValueChange={setTld}>
                <SelectTrigger id="tld" className="w-32"><SelectValue placeholder="选择" /></SelectTrigger>
                <SelectContent>
                  {tlds.map((t) => <SelectItem key={t.tld} value={t.tld}>.{t.tld}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="count" className="text-xs">域名数</Label>
              <Input
                id="count" className="w-28" inputMode="numeric"
                value={count} onChange={(e) => setCount(e.target.value)}
              />
            </div>

            <Button onClick={onLoad} disabled={load.isPending || brake !== null}>
              <Play className="mr-2 h-4 w-4" aria-hidden />
              2. 造压力
            </Button>

            <Button variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
              刷新读数
            </Button>
          </div>

          {brake && (
            <p role="alert" className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{brake}</span>
            </p>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              解析队列积压 <strong className={data.resolve_backlog > data.max_backlog_forload ? 'text-destructive' : 'text-foreground'}>
                {formatNumber(data.resolve_backlog)}
              </strong> / {formatNumber(data.max_backlog_forload)}
            </span>
            <span>拒绝率上限 {Math.round(data.max_rejection_rate * 100)}%</span>
            <span>踩线冷却 {data.cooldown_minutes} 分钟</span>
          </div>

          <p className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              加压会把这些域名的下次确认时间提前，消耗真实外部配额；确认完成后自动恢复正常排程。
              只影响 L0/L1 域名，不会动到即将释放的关键域名。删除窗口期内会被拒绝执行。
              第一轮建议用 500 条、<strong>先不要改 qps</strong>，确认能看到「未打满 → 自我限制中」的转变，
              再开始逐档调高。
            </span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>RDAP 主机</CardTitle>
          <CardDescription>
            统计窗口 {Math.round(data.window_seconds / 60)} 分钟。
            共用同一台服务器的 TLD 合并为一行，因为它们共用同一个令牌桶。
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[52rem] text-sm">
            <thead className="border-b text-left text-xs text-muted-foreground">
              <tr>
                <th className="pb-2 font-medium">主机 / TLD</th>
                <th className="pb-2 text-right font-medium">配置 qps</th>
                <th className="pb-2 text-right font-medium">请求</th>
                <th className="pb-2 text-right font-medium">我方延迟</th>
                <th className="pb-2 text-right font-medium">上游拒绝</th>
                <th className="pb-2 font-medium">结论 / 下一步</th>
              </tr>
            </thead>
            <tbody>
              {data.rdap_hosts.map((h) => {
                const v = VERDICT[h.verdict]
                return (
                  <tr key={h.host} className="border-b last:border-0 align-top">
                    <td className="py-3">
                      <div className="font-medium">{h.host}</div>
                      <div className="text-xs text-muted-foreground">
                        {h.tlds.map((t) => `.${t}`).join(' ')}
                        {h.circuit_breaker && h.circuit_breaker !== 'closed' && (
                          <Badge variant="danger" className="ml-2">熔断 {h.circuit_breaker}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3">
                      <BudgetEditor host={h} />
                    </td>
                    <td className="py-3 text-right tabular-nums">{formatNumber(h.attempts)}</td>
                    <td className="py-3 text-right tabular-nums">{formatNumber(h.deferred_by_us)}</td>
                    <td className={`py-3 text-right tabular-nums ${h.rejected_upstream > 0 ? 'font-semibold text-red-600 dark:text-red-400' : ''}`}>
                      {formatNumber(h.rejected_upstream)}
                    </td>
                    <td className="py-3">
                      <Badge variant={v.variant}>{v.label}</Badge>
                      <p className="mt-1 max-w-sm text-xs text-muted-foreground">{v.next}</p>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>WHOIS 服务器</CardTitle>
          <CardDescription>{data.whois_note}</CardDescription>
        </CardHeader>
        <CardContent>
          {data.whois_servers.length === 0 ? (
            <p className="text-sm text-muted-foreground">窗口内没有 WHOIS 调用。</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="pb-2 font-medium">服务器</th>
                  <th className="pb-2 text-right font-medium">配置 qps</th>
                  <th className="pb-2 text-right font-medium">请求</th>
                  <th className="pb-2 text-right font-medium">我方延迟</th>
                </tr>
              </thead>
              <tbody>
                {data.whois_servers.map((s) => (
                  <tr key={s.server} className="border-b last:border-0">
                    <td className="py-2 font-medium">{s.server}</td>
                    <td className="py-2 text-right tabular-nums">{s.configured_qps}</td>
                    <td className="py-2 text-right tabular-nums">{formatNumber(s.attempts)}</td>
                    <td className="py-2 text-right tabular-nums">{formatNumber(s.deferred_by_us)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
